/*
 * @Author: xiedong.61
 * @Email: xiedong.61@jd.com
 * @Date: 2025-12-04 14:10:27
 * @LastEditors: xiedong.61
 * @LastEditTime: 2025-12-05 15:12:02
 * @Description: 自适应标签显示
 */
import { computed, nextTick, ref, watch } from 'vue'
import { useNamespace } from '@jdesign-vue/hooks'
import type { ComputedRef, Ref } from 'vue'

// 默认最大折叠标签数量
const MAX_COLLAPSE_TAGS_DEFAULT = 1

interface MaxCollapseTagsProps {
  checkedNodes: ComputedRef<any>
  tagWrapper: Ref<HTMLElement | null>
  maxCollapseTags: ComputedRef<number | string>
}

export const useMaxCollapesTags = (props: MaxCollapseTagsProps) => {
  const { checkedNodes, tagWrapper, maxCollapseTags } = props

  const autoMaxCollapseTags = ref(MAX_COLLAPSE_TAGS_DEFAULT)
  const nsCascader = useNamespace('cascader')
  const tagMaxWidth = ref<number | undefined>()

  const dynamicMaxCollapseTags = computed(() => {
    if (
      typeof maxCollapseTags.value === 'string' &&
      maxCollapseTags.value !== 'auto'
    ) {
      return MAX_COLLAPSE_TAGS_DEFAULT
    }

    return maxCollapseTags.value === 'auto'
      ? autoMaxCollapseTags.value
      : Math.min(Number(maxCollapseTags.value || 1), autoMaxCollapseTags.value)
  })

  /**
   * @description 计算出标签的最大宽度，用于设置标签的maxWidth样式
   * @returns 标签的最大宽度
   */
  const updateTagMaxWidth = async () => {
    await nextTick()
    const elTagWrapper = tagWrapper.value
    if (!elTagWrapper) {
      tagMaxWidth.value = undefined
      return
    }
    // tags容器宽度
    const elWrapperWidth = elTagWrapper.getBoundingClientRect().width
    const elWrapperPaddingLeft =
      Number.parseInt(getComputedStyle(elTagWrapper).paddingLeft) || 0
    const elWrapperPaddingRight =
      Number.parseInt(getComputedStyle(elTagWrapper).paddingRight) || 0
    const elLastCountTag = elTagWrapper.querySelector(
      `.${nsCascader.e('tag')}:not(.is-closable)`
    )
    if (!elLastCountTag) {
      tagMaxWidth.value = undefined
      return
    }
    // 最后一个标签宽度
    const elLastCountTagWidth =
      elLastCountTag?.getBoundingClientRect?.()?.width || 0
    const elLastCountTagMarginLeft =
      Number.parseInt(getComputedStyle(elLastCountTag).marginLeft) || 0
    const elLastCountTagMarginRight =
      Number.parseInt(getComputedStyle(elLastCountTag).marginRight) || 0

    tagMaxWidth.value =
      elWrapperWidth -
      elLastCountTagWidth -
      elWrapperPaddingLeft -
      elWrapperPaddingRight -
      // margin减去两倍，因为标签之间有margin
      elLastCountTagMarginLeft * 2 -
      elLastCountTagMarginRight * 2
  }

  watch(
    () => [checkedNodes.value?.length, maxCollapseTags.value],
    (newValues) => {
      if (
        typeof maxCollapseTags.value === 'undefined' ||
        (typeof maxCollapseTags.value === 'string' &&
          maxCollapseTags.value !== 'auto')
      ) {
        return
      }

      const wrapperEl = tagWrapper.value
      if (!wrapperEl) return
      // 选中的数量发生变化时，粗略计算能容纳的标签数量
      const elSelectionWidth = wrapperEl.getBoundingClientRect().width

      let roughWidth = 40
      // 使用标签的宽度来计算能容纳的标签数量
      const elCascaderTagItem = wrapperEl.querySelector(
        `.${nsCascader.e('tag')}`
      )
      if (elCascaderTagItem) {
        const elItemWidth = elCascaderTagItem.getBoundingClientRect().width
        const marginRight = elCascaderTagItem
          ? Number.parseInt(getComputedStyle(elCascaderTagItem).marginRight)
          : 0
        roughWidth = elItemWidth + marginRight * 2
      }
      const roughCount = Math.ceil(elSelectionWidth / roughWidth)
      // 优化计算，如果标签数量大于当前标签数量，则不进行计算，减少重复渲染
      if (
        newValues[0] > roughCount &&
        roughCount > autoMaxCollapseTags.value &&
        dynamicMaxCollapseTags.value > 1
      ) {
        autoMaxCollapseTags.value = dynamicMaxCollapseTags.value
        return
      }
      autoMaxCollapseTags.value = roughCount
    },
    {
      flush: 'post',
    }
  )

  watch(
    () => [
      autoMaxCollapseTags.value,
      checkedNodes.value,
      maxCollapseTags.value,
    ],
    async () => {
      if (
        typeof maxCollapseTags.value === 'undefined' ||
        (typeof maxCollapseTags.value === 'string' &&
          maxCollapseTags.value !== 'auto')
      ) {
        return
      }

      if (autoMaxCollapseTags.value <= 1) return

      await nextTick()

      const elTagWrapper = tagWrapper.value
      if (!elTagWrapper) return

      const elCascaderTagItem = elTagWrapper.querySelector(
        `.${nsCascader.e('tag')}`
      )
      if (!elCascaderTagItem) return
      // 测试是否存在换行
      const elWrapperHeight = elTagWrapper.getBoundingClientRect().height
      const elCascaderTagItemHeight =
        elCascaderTagItem?.getBoundingClientRect().height
      const hasWrap = elWrapperHeight > elCascaderTagItemHeight * 2

      if (hasWrap) {
        // 有换行，减少显示数量
        autoMaxCollapseTags.value -= 1
      }
    },
    {
      flush: 'post',
      deep: true,
    }
  )

  watch(
    () => [checkedNodes.value?.length, dynamicMaxCollapseTags.value],
    async () => {
      await updateTagMaxWidth()
    },
    {
      flush: 'post',
      immediate: true,
    }
  )

  return {
    tagMaxWidth,
    dynamicMaxCollapseTags,
  }
}
